import { TranslationKey } from './../../../../../types/translation/TranslationKey';
import React, { useContext, useState } from 'react';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { t } from '../../../../../types/translation/Translator';
import CreateOrderProductPane from './Panes/CreateOrderProductPane';
import { useLazyQuery } from '@apollo/client';
import {
  AddOrderProductResponse,
  AddOrderProductVariables,
  OrderMutations,
} from '../../../../../graphql/order.graphql';
import { Order } from '../../../../../types/order/order';
import { testIds } from '../../../../../util/identifiers/identifiers.util';
import Modal from '../../../../../VentoryUI/components/common/Modal/Modal';
import {
  GetProductTransactionsResponse,
  GetProductTransactionsVariables,
  ProductTransactionQueries,
} from '../../../../../graphql/productTransaction.graphql';
import { ProductTransaction, ProductTransactionSpecifiers } from '../../../../../types/productTransaction';
import { ProductTransactionContext } from '../../../../../context/ProductTransactionContext';
import { OrderInput } from '../../../../../types/order/';
import { useToast } from '../../../../../providers/ToastProvider';
import { ToastType } from '../../../../components/Toast';
import useMutation from '../../../../../hooks/useMutation';
import { DomUtils } from '../../../../../util/dom.util';

interface AddOrderProductsModalInputProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  order: Order;
}

export default function AddOrderProductsModal({ open, setOpen, order }: AddOrderProductsModalInputProps) {
  if (!open) return null;

  const { showToast } = useToast();

  const { currentCompany } = useContext(CompanyContext);
  const { productTransactions, setProductTransactions } = useContext(ProductTransactionContext);

  const [orderInput, setOrderInput] = useState<OrderInput>(
    new OrderInput({
      companyId: currentCompany.id,
      id: order.id,
      toStockLocationId: order.toStockLocationId,
      fromStockLocationId: order.fromStockLocationId,
      type: order.type,
    }),
  );

  const [create, { loading }] = useMutation<AddOrderProductResponse, AddOrderProductVariables>(
    OrderMutations.addOrderProduct,
    {
      onCompleted: res => {
        fetchProductTransactions();
        showToast({
          type: ToastType.success,
          title: t(TranslationKey.productAddedToOrderSuccessfullyToastTitle).singular.upper,
        });
      },
    },
  );

  const [fetchProductTransactions, { loading: productTransactionsLoading }] = useLazyQuery<
    GetProductTransactionsResponse,
    GetProductTransactionsVariables
  >(ProductTransactionQueries.get, {
    variables: {
      companyId: currentCompany.companyId,
      batchSize: orderInput.products.size < 100 ? 100 : orderInput.products.size,
    },
    onCompleted: async res => {
      res.productTransactions.data.forEach(pt => productTransactions.set(pt.id, new ProductTransaction(pt)));
      setProductTransactions(new Map(productTransactions));
      handleClose();
    },
  });

  const handleClose = () => {
    setOrderInput(
      new OrderInput({
        companyId: currentCompany.id,
        id: order.id,
        toStockLocationId: order.toStockLocationId,
        fromStockLocationId: order.fromStockLocationId,
        type: order.type,
      }),
    );
    setOpen(false);
  };

  const handleFinish = async () => {
    await create({
      variables: {
        orderId: order.id,
        specifiers: [...orderInput.products.values()].map(p => {
          const copy = new ProductTransactionSpecifiers(p);
          delete copy.id;
          (copy as any).fromCustomFields = Array.from(copy.fromCustomFields.values());
          (copy as any).toCustomFields = Array.from(copy.toCustomFields.values());
          return copy;
        }),
      },
    });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      height={DomUtils.getModalHeight()}
      title={t(TranslationKey.addProductsToOrder).singular.label}
      testId={testIds.createOrderModal}
    >
      <CreateOrderProductPane
        setError={() => {}}
        order={orderInput}
        loading={loading || productTransactionsLoading}
        setOrder={setOrderInput}
        add={handleFinish}
        cancel={handleClose}
      />
    </Modal>
  );
}
